import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from "axios";
import { fetchRefreshToken } from "models/auth-client/controllers/auth.controller";
import { store } from "../store";
import { local, logout, setUserAuthTokens, setTempToken } from "../store/actions/user";

declare module "axios" {
  // eslint-disable-next-line no-shadow
  export interface AxiosRequestConfig {
    requireAuth?: boolean;
  }
}

const onRequest = (config: AxiosRequestConfig) => {
  /* eslint-disable prefer-destructuring */
  const accessToken = store.getState().userReducer.accessToken;
  const tempToken = store.getState().userReducer.tempToken;
  const token = accessToken && accessToken !== "" ? accessToken : tempToken;
  if (token  && config.requireAuth) {
    // eslint-disable-next-line no-param-reassign
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
};

const TEMP_TOKEN_ENDPOINTS = ["auth-client/otp/validate", "auth-client/temp-token"];

const onResponse = (response: AxiosResponse) => {
  const isTempTokenEndpoint = TEMP_TOKEN_ENDPOINTS.some((path) =>
    response.config.url?.includes(path)
  );

  if (isTempTokenEndpoint && response.data.token && response.data.expiresIn) {
    store.dispatch(setTempToken(response.data.token));
    return response;
  }

  if (response.data.jwt || response.data.token) {
    store.dispatch(
      setUserAuthTokens(
        response.data.jwt || response.data.token,  // field name from API response
        response.data.refresh_token ?? ""
      )
    );
  }
  if (response.data.user || response.data.ozoner) {
    store.dispatch(local(response.data.user || response.data.ozoner));
  }
  return response;
};

let isForcingLogout = false;

const forceLogoutToLogin = () => {
  if (isForcingLogout) return;
  isForcingLogout = true;

  try {
    store.dispatch(logout());
  } catch (e) {
    // best-effort only; the storage wipe below is what actually matters
  }

  try {
    localStorage.clear();
  } catch (e) {
    // ignore (private browsing / storage disabled)
  }

  window.location.href = "/";
};

const onResponseError = (instance: AxiosInstance) => async (error: any) => {
  try {
    const originalRequest = error.config;

    if (originalRequest.headers && originalRequest.headers.refreshRequest) {
      // if the failed request was a refresh request, do not attempt to refresh token
      throw error;
    }

    const { accessToken, refreshToken } = store.getState().userReducer;

    if (error.response?.status === 401 && !originalRequest._retry && accessToken) {
      try {
        originalRequest._retry = true;
        await fetchRefreshToken(refreshToken);
        return instance(originalRequest);
      } catch(err) {
        if (originalRequest.url !== "auth-client/login") {
          forceLogoutToLogin();
        }
      }
    }

    throw error;
  } catch (e) {
    return Promise.reject(error);
  }
};

const PandaboardApiInstance = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL_PA, // "https://api-pandaboard.dev.ozon.mobi/v1/"
});

PandaboardApiInstance.interceptors.request.use(onRequest);
PandaboardApiInstance.interceptors.response.use(
  onResponse,
  onResponseError(PandaboardApiInstance)
);

export { PandaboardApiInstance };
